import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { FLOW_NODE_KIND_ICON } from "@/components/flow/flow-node-title"
import type { FlowSidePanelProps } from "@/components/flow/flow-side-panel-props"
import { FlowVideoSourceSlot, flowVideoSourceName } from "@/components/flow/flow-video-source-slot"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { ParametersSchemaForm } from "@/components/shot-panel/parameters-schema-form"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import { AgentColumnHeader } from "@/components/workspace/agent-column-header"
import { useGenerationPrice } from "@/hooks/use-generation-price"
import { useTranslation } from "@/hooks/use-translation"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { updateFlowNode, upscaleFlowNode } from "@/lib/flow-api"
import {
  defaultsFromPostProductionSchema,
  firstPostProductionParameterErrorKey,
  parsePostProductionParameterServerError,
  scrollToPostProductionParameterField,
  validatePostProductionParameters,
  type PostProductionParameterErrors,
} from "@/lib/post-production-parameters"
import type {
  PostProductionModelOption,
  PostProductionParameters,
  PostProductionParametersSchema,
} from "@/lib/workspace-api"
import { flowNodePending } from "@/types/flow"

type Props = FlowSidePanelProps & {
  models: PostProductionModelOption[]
}

function enabledFamilies(models: PostProductionModelOption[]) {
  return models.filter((model) => !model.platform_disabled && model.versions.length > 0)
}

function familyForCode(models: PostProductionModelOption[], modelId: string) {
  return models.find((model) => model.versions.some((version) => version.api_model_code === modelId)) ?? null
}

function paramsFor(schema: PostProductionParametersSchema, stored: Record<string, string>): PostProductionParameters {
  const defaults = defaultsFromPostProductionSchema(schema)
  const overlay: PostProductionParameters = {}
  for (const [key, value] of Object.entries(stored)) {
    if (value == null || value === "") continue
    overlay[key] = value
  }
  return { ...defaults, ...overlay }
}

function stringifyParams(params: PostProductionParameters): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue
    out[key] = String(value)
  }
  return out
}

export function FlowUpscalePanel({
  node,
  nodes,
  models,
  projectId,
  frameFormat,
  frameRate,
  canEdit,
  onDisconnectEdge,
  onNodeChange,
  onAttach,
}: Props) {
  const { t } = useTranslation("flow")
  const { t: tShot } = useTranslation("shot")
  const families = useMemo(() => enabledFamilies(models), [models])
  const [modelId, setModelId] = useState("")
  const [params, setParams] = useState<PostProductionParameters>({})
  const [errors, setErrors] = useState<PostProductionParameterErrors>({})
  const [generating, setGenerating] = useState(false)
  const saveTimer = useRef<number | null>(null)
  const nodeIdRef = useRef(node.id)
  nodeIdRef.current = node.id

  const family = familyForCode(families, modelId) ?? families[0] ?? null
  const version =
    family?.versions.find((item) => item.api_model_code === modelId) ??
    family?.versions.find((item) => item.default_version) ??
    family?.versions[0] ??
    null
  const schema = family?.parameters_schema ?? {}
  const sourceInput = (node.inputs ?? []).find(
    (input) => input.target_port === "video" || input.source_port === "video",
  )
  const source = sourceInput ? nodes.find((row) => row.id === sourceInput.source_node_id) : undefined
  const pending = flowNodePending(node) || generating
  // Секунды источника считает сервер тем же правилом, что и заморозка; ключ — чтобы цена
  // перечиталась, когда у источника сменилась версия или файл.
  const sourceQuoteKey = source
    ? [source.id, source.duration_ms ?? "", source.media_url ?? "", source.generation_status ?? ""].join(":")
    : ""

  useEffect(() => {
    const match = familyForCode(families, node.model_id ?? "")
    const nextFamily = match ?? families[0] ?? null
    const nextVersion =
      nextFamily?.versions.find((item) => item.api_model_code === node.model_id) ??
      nextFamily?.versions.find((item) => item.default_version) ??
      nextFamily?.versions[0] ??
      null
    setModelId(nextVersion?.api_model_code ?? "")
    setParams(paramsFor(nextFamily?.parameters_schema ?? {}, node.model_params))
    setErrors({})
    // Ресид только при смене ноды: черновик параметров живёт локально, пока она открыта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id])

  useEffect(() => {
    return () => {
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
    }
  }, [])

  const persist = (nextModelId: string, nextParams: PostProductionParameters) => {
    if (!canEdit) return
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
    const nodeId = node.id
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null
      void updateFlowNode(projectId, nodeId, {
        model_id: nextModelId,
        model_params: stringifyParams(nextParams),
      })
        .then((next) => {
          if (nodeIdRef.current === nodeId) onNodeChange(next)
        })
        .catch((error: unknown) => {
          toast.error(error instanceof Error ? error.message : t("errors.nodeFailed"))
        })
    }, 500)
  }

  const selectFamily = (familyId: string) => {
    const nextFamily = families.find((item) => String(item.id) === familyId)
    if (!nextFamily) return
    const nextVersion = nextFamily.versions.find((item) => item.default_version) ?? nextFamily.versions[0]
    if (!nextVersion) return
    const nextParams = defaultsFromPostProductionSchema(nextFamily.parameters_schema)
    setModelId(nextVersion.api_model_code)
    setParams(nextParams)
    setErrors({})
    persist(nextVersion.api_model_code, nextParams)
  }

  const selectVersion = (code: string) => {
    if (!family) return
    const nextParams = defaultsFromPostProductionSchema(family.parameters_schema)
    setModelId(code)
    setParams(nextParams)
    setErrors({})
    persist(code, nextParams)
  }

  const price = useGenerationPrice({
    kind: "flow_upscale",
    projectId,
    enabled: Boolean(version && source && !pending),
    params: {
      node_id: node.id,
      model_id: version?.api_model_code ?? "",
      quote_key: sourceQuoteKey,
    },
  })

  const handleGenerate = () => {
    if (!canEdit || pending || !version || !sourceInput) return
    const nextErrors = validatePostProductionParameters(schema, params, tShot)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      const key = firstPostProductionParameterErrorKey(schema, nextErrors)
      if (key) scrollToPostProductionParameterField(key)
      return
    }
    setErrors({})
    setGenerating(true)
    if (saveTimer.current != null) {
      window.clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    void upscaleFlowNode(projectId, node.id, { model_id: version.api_model_code, parameters: params })
      .then((next) => onNodeChange(next))
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t("errors.nodeFailed")
        const parsed = parsePostProductionParameterServerError(message)
        if (parsed.field) {
          setErrors({ [parsed.field]: parsed.message })
          scrollToPostProductionParameterField(parsed.field)
        }
        toast.error(message)
      })
      .finally(() => setGenerating(false))
  }

  const sourceLabels = { video: t("upscale.video"), upscale: t("menu.upscale"), depth: t("menu.depth") }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AgentColumnHeader icon={FLOW_NODE_KIND_ICON.video_upscale} title={t("menu.upscale")} />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <FlowVideoSourceSlot
            label={t("upscale.source")}
            mediaLabel={t("chain.video")}
            emptyHint={t("upscale.needsSource")}
            uploadLabel={t("chain.upload")}
            libraryLabel={t("chain.library")}
            disconnectLabel={t("upscale.disconnect")}
            source={source}
            sourceName={source ? flowVideoSourceName(source, sourceLabels) : t("upscale.video")}
            connected={Boolean(sourceInput)}
            canEdit={canEdit}
            disabled={pending}
            projectId={projectId}
            frameFormat={frameFormat}
            frameRate={frameRate}
            onDisconnect={() => {
              if (sourceInput) onDisconnectEdge(sourceInput.id)
            }}
            onAttach={onAttach}
          />
          {families.length === 0 ? (
            <p className="text-sm text-ui-text-muted">{t("upscale.noModels")}</p>
          ) : (
            <>
              <SelectDropdown
                value={family ? String(family.id) : ""}
                onChange={selectFamily}
                options={families.map((item) => ({ value: String(item.id), label: item.name }))}
                disabled={!canEdit || pending}
                ariaLabel={t("upscale.model")}
                className="w-full"
              />
              {family && family.versions.length > 1 ? (
                <SelectDropdown
                  value={version?.api_model_code ?? ""}
                  onChange={selectVersion}
                  options={family.versions.map((item) => ({ value: item.api_model_code, label: item.name }))}
                  disabled={!canEdit || pending}
                  ariaLabel={t("upscale.version")}
                  className="w-full"
                />
              ) : null}
              <ParametersSchemaForm
                schema={schema}
                value={params}
                errors={errors}
                onChange={(next) => {
                  setParams(next)
                  setErrors({})
                  if (version) persist(version.api_model_code, next)
                }}
              />
            </>
          )}
        </div>
        {canEdit ? (
          <GenerateWithModelLabel
            label={t("column.generate")}
            busyLabel={t("column.generating")}
            modelLabel={version?.name}
            cost={price}
            costPlacement="tooltip"
            busy={pending}
            disabled={node.batch_waiting === true || !version || !sourceInput}
            disabledReason={node.batch_waiting ? t("bulk.queued") : undefined}
            onClick={handleGenerate}
            confirmDelayKey={confirmDelayKey("flow", node.id, "upscale")}
            size="lg"
            className="min-h-[calc(3rem+0.25rem+13px)] w-full shrink-0 items-stretch"
            buttonClassName="w-full"
          />
        ) : null}
      </div>
    </div>
  )
}
